import { useEffect, useReducer, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useRouter } from 'next/router';

import { cleanStateQuery, handlePathToQuery } from '@spa/shared/utils/queryParams';
import { makeKey } from '@spa/shared/utils/key';

const SET_STATE = 'set_state';
const oldSearchParams = ['south', 'west', 'north', 'east'];

const hasOldParameter = parameter => oldSearchParams.find(oldParameter => oldParameter === parameter);

const hasCoordinatesFromOldSearch = changedParams =>
  changedParams.filter(hasOldParameter).length === oldSearchParams.length;

export const getStateFromOldQuery = ({ south, west, north, east, ...state }) => {
  const sw = `${south},${west}`;
  const ne = `${north},${east}`;

  return { ...state, sw, ne };
};

export const getState = ({ state, key }, query, changedParams) => ({
  state: hasCoordinatesFromOldSearch(changedParams) ? getStateFromOldQuery(state, query) : state,
  key: changedParams.length > 0 ? makeKey() : key
});

const getChangedParams = (prev = {}, current) => {
  const differentValues = key => !isEqual(prev[key], current[key]);

  const keys = [
    ...new Set([...Object.keys(prev).filter(differentValues), ...Object.keys(current).filter(differentValues)])
  ];

  return keys;
};

const reducer = (state = {}, { type, payload }) => {
  switch (type) {
    case SET_STATE:
      return { ...state, ...payload, changedParams: getChangedParams(state.query, payload.query) };
    default:
      return state;
  }
};

/**
 * searchQueryParams = invisible params + query-params from our search
 * it manages the difference between the requests we need to make to our search
 * and the ones we use on our search page
 */
export const useSearchHistory = keyFromServer => {
  const router = useRouter();
  const {
    query: { ...queryParams }
  } = router;
  const [searchState] = useState({ key: keyFromServer });
  const query = cleanStateQuery({ ...queryParams, ...handlePathToQuery(queryParams.city) });
  const changedParams = Object.keys(query);
  const { state, key } = getState({ state: { ...searchState, ...query } }, {}, changedParams);
  const [historyState, dispatch] = useReducer(reducer, { state, query, changedParams, key });

  useEffect(() => {
    const newState = key ? { ...state, key } : state;
    const payload = { query, state: newState };

    dispatch({ type: SET_STATE, payload });
  }, [router.query]);

  return historyState;
};
